import { Link } from "react-router-dom";
import { motion } from "framer-motion";
import {
  Chapter,
  Body,
  Prose,
  Ledger,
  PullQuote,
  Kicker,
} from "@/components/site/Editorial";

const ease = [0.22, 1, 0.36, 1];

const MEMORIAL_ILLUSTRATION =
  "https://images.unsplash.com/photo-1760201656695-76e8765eef67?crop=entropy&cs=srgb&fm=jpg&ixid=M3w4NjA0MTJ8MHwxfHNlYXJjaHw0fHxwZWFjZWZ1bCUyMG5hdHVyZSUyMG1lbW9yaWFsfGVufDB8fHx8MTc4NTYwNDA5Nnww&ixlib=rb-4.1.0&q=85";

export default function CelebrateALifeArticle() {
  return (
    <article data-testid="celebrate-article">
      {/* Article opener */}
      <section className="max-w-[1600px] mx-auto px-6 md:px-10 pt-4 pb-14 md:pb-20">
        <div className="grid grid-cols-12 gap-8 md:gap-14 items-end">
          <div className="col-span-12 md:col-span-7">
            <Kicker chapter="Wing II" minutes={12} dateline="An editorial" />
            <motion.h2
              initial={{ opacity: 0, y: 20 }}
              whileInView={{ opacity: 1, y: 0 }}
              viewport={{ once: true }}
              transition={{ duration: 0.9, ease }}
              className="mt-6 heritage-title leading-[0.98] tracking-[-0.01em]"
              style={{ fontSize: "clamp(40px, 5.4vw, 92px)" }}
            >
              To remember well
              <br />
              <span className="font-italic-serif italic text-[color:var(--rust)]">
                is a family art.
              </span>
            </motion.h2>
            <p className="mt-8 max-w-[52ch] text-[17px] md:text-[19px] leading-[1.65] text-[color:var(--ink-soft)] font-italic-serif italic">
              Grief is not a project you complete. It is a rhythm you learn to
              live inside. This is an editorial about the small, useful rituals
              of remembrance — how to write an obituary, how to hold an
              anniversary, and how to give a life the shape of a tribute.
            </p>
          </div>

          <div className="col-span-12 md:col-span-5">
            <div className="vintage-frame">
              <div className="aspect-[4/5] overflow-hidden">
                <img src={MEMORIAL_ILLUSTRATION} alt="A candle in a still room" />
              </div>
              <div className="absolute bottom-2 left-0 right-0 text-center font-italic-serif italic text-[11px] text-[color:var(--sepia)]">
                A rose left in the quiet
              </div>
            </div>
          </div>
        </div>
      </section>

      {/* 01 · What a memorial is (and is not) */}
      <Chapter n="01" title="What a memorial" subtitle="is (and is not).">
        <Prose>
          <Body>
            A memorial page is not a headstone, and it is not a status
            update. It is the room a family opens for a person after their
            body has been laid to rest — a room that stays warm even when
            nobody is inside. Anyone in the family can walk in, light a
            candle, leave a sentence and walk out. The room outlasts the
            phone, the platform, and, we hope, the grief.
          </Body>
          <Body>
            The best memorials do three things. They tell the truth of a
            life without varnishing it. They give the family a place to
            gather that is not a WhatsApp thread. And they let strangers —
            colleagues, students, neighbours — leave the sentence they
            couldn't say at the funeral.
          </Body>
          <PullQuote attribution="Rainer Maria Rilke">
            The only journey is the one within — but sometimes another's life
            makes the map.
          </PullQuote>
        </Prose>
      </Chapter>

      {/* 02 · The shape of a tribute */}
      <Chapter n="02" title="The shape of" subtitle="a tribute.">
        <Prose>
          <Body>
            A tribute is not a résumé. It is not a life reduced to its
            promotions and its degrees. The shape that works, over and over,
            is the following five-beat arc — short enough to be read at a
            funeral, long enough to hold a life.
          </Body>
          <Ledger
            items={[
              "The scene we remember them in — a chair, a kitchen, a boat, a schoolroom.",
              "One line about where they came from — the village, the family, the accent.",
              "Two or three small habits that were unmistakably theirs.",
              "The one thing they taught you that has held.",
              "A blessing, an image, or a wish sent onwards.",
            ]}
          />
          <Body>
            A good tribute is a portrait, not a summary. Give it a scene.
            Give it a hand gesture. Give it a proper noun. The reader who
            never met them should be able to close their eyes and see them.
          </Body>
        </Prose>
      </Chapter>

      {/* 03 · Obituary etiquette */}
      <Chapter n="03" title="Obituary" subtitle="etiquette.">
        <Prose>
          <Body>
            Newspapers are shrinking; obituaries have moved online. The
            manners, however, have not changed. A published notice is read
            by people who last saw the deceased forty years ago — a
            classmate, a first employer, a landlord's son. Write it for
            them, not for you.
          </Body>
          <Ledger
            items={[
              "Full name at birth, with any maiden name in parentheses.",
              "Date of death, place, and (if the family wishes) cause.",
              "The names of the parents, the spouse, and the children.",
              "The single sentence that describes the working life.",
              "Time and place of the funeral, wake, or memorial service.",
              "How to send condolences, flowers, or a donation in lieu.",
            ]}
          />
          <Body>
            Some families print full obituaries. Others prefer a short
            notice and let the memorial page carry the fuller story. Both
            are correct. What is not correct is silence: an obituary is a
            small courtesy to the people who once shared the world with the
            deceased and now have to learn it is missing them.
          </Body>
          <PullQuote attribution="An old family rule">
            Never bury a story with a person. Someone, somewhere, is still
            reading them.
          </PullQuote>
        </Prose>
      </Chapter>

      {/* 04 · Anniversaries and rites of remembrance */}
      <Chapter n="04" title="Anniversaries and" subtitle="rites of remembrance.">
        <Prose>
          <Body>
            Every tradition has its rite. The Catholic Mass on the month's
            mind. The Jewish yahrzeit candle on the eve of the Hebrew date.
            The Hindu shraddha on the tithi. The Muslim Isaal-e-Sawaab on
            the Friday nearest. The Buddhist merit-making on the 49th day.
            The West African libation on the annual gathering.
          </Body>
          <Body>
            The rites are different; the instinct is the same. Once a year,
            the family agrees to stop, together, and say a name. On a
            memorial page you can set that day quietly and let the reminder
            arrive without fuss — a candle relit, a message added, a photo
            uploaded.
          </Body>
          <Ledger
            items={[
              "The one-month mind — 30 days after the passing.",
              "The first anniversary — a slower, family-only ceremony.",
              "The birthday — sometimes harder than the death-day.",
              "The wedding date — for a widow or widower, a private observance.",
              "The seasonal date — the recipe cooked, the walk taken, the song played.",
              "The distant relatives' annual — the day one whole branch of the family gathers by video.",
            ]}
          />
        </Prose>
      </Chapter>

      {/* 05 · Grief, at your own pace */}
      <Chapter n="05" title="Grief, at" subtitle="your own pace.">
        <Prose>
          <Body>
            Grief does not have a shape. It has a texture. Some months you
            can name it and set it aside; some afternoons it walks in
            unannounced, past whatever door you have built. This is not a
            failure of composure. It is the ordinary weather of loving
            someone who is gone.
          </Body>
          <Body>
            A memorial page is useful precisely because it can be visited
            quietly, without an audience. Light a candle on the six-month
            morning when nobody has called. Add a line to the guestbook when
            a stranger says something kind about your father. Small,
            unwitnessed acts of remembrance are the ones that carry us
            furthest.
          </Body>
          <PullQuote attribution="Anne Lamott">
            You will lose someone you can't live without, and your heart will
            be badly broken, and the bad news is that you never completely
            get over the loss. But this is also the good news.
          </PullQuote>
        </Prose>
      </Chapter>

      {/* 06 · How memorials on FamilyTree.Team work */}
      <Chapter n="06" title="How memorials on" subtitle="FamilyTree.Team work.">
        <Prose>
          <Body>
            Every person you add to your family archive can have a memorial
            page opened for them — before or after their passing. The page
            has three quiet features: a lit-candle grid where any member of
            the family can add a candle (and an optional whispered note); a
            guestbook where longer messages accumulate over the years; and a
            small portrait framed the way a vintage photograph would be.
          </Body>
          <Body>
            Memorial pages are private to the family archive by default. A
            member with the right role can make one public if the family
            wishes to invite classmates, colleagues and readers to leave
            their own remembrance.
          </Body>

          <div className="mt-8 flex flex-wrap gap-4">
            <Link
              to="/app/register"
              data-testid="celebrate-article-cta"
              className="btn-wood"
            >
              Open a memorial <span aria-hidden>→</span>
            </Link>
            <Link to="/archive" className="btn-ghost">
              Return to the archive
            </Link>
          </div>
        </Prose>
      </Chapter>
    </article>
  );
}
